import {useCallback} from 'react';

import {
  trackWorkspaceError as trackWorkspaceErrorEvent,
  type WorkspaceErrorAction,
  type WorkspaceErrorType,
} from '../../services/analytics';
import {useViewer} from '../../state/viewer/hooks';
import {type LoadableView} from '../../state/views/types';
import {type WorkspaceError} from '../../state/workspaces/types';
import {viewerIsWbAdminUsingPrivileges} from '../../util/admin';
import {recordRumError} from '../../util/datadog/datadogRum';

const UNKNOWN_ERROR_MESSAGE = 'unknown error';

type TrackableError = {
  message: string;
  name?: string;
  errorCode?: string;
};

const isWorkspaceError = (error: object): error is WorkspaceError =>
  'errorCode' in error || 'userMessage' in error || 'userTitle' in error;

/**
 * Normalizes whatever a call site hands us into an object with an own `message`.
 *
 * The analytics payload is JSON-serialized, and `Error` keeps `message`/`name`
 * on non-enumerable properties, so an `Error` passed straight through arrives
 * downstream as `{}`. Workspace load failures carry `userMessage` rather than
 * `message`, which is the other case where `error.message` ends up unset.
 */
export function toTrackableError(error: unknown): TrackableError {
  if (error instanceof Error) {
    return {
      message: error.message || UNKNOWN_ERROR_MESSAGE,
      name: error.name,
    };
  }
  if (typeof error === 'string') {
    return {message: error || UNKNOWN_ERROR_MESSAGE};
  }
  if (error != null && typeof error === 'object' && isWorkspaceError(error)) {
    return {
      message: error.userMessage ?? error.userTitle ?? UNKNOWN_ERROR_MESSAGE,
      errorCode: error.errorCode,
    };
  }

  return {message: error == null ? UNKNOWN_ERROR_MESSAGE : String(error)};
}

export function useTrackWorkspaceError({
  action,
  errorType,
  viewType,
}: {
  action: WorkspaceErrorAction;
  errorType: WorkspaceErrorType;
  viewType: LoadableView['type'] | undefined;
}) {
  const viewer = useViewer();

  const trackWorkspaceError = useCallback(
    (error?: unknown) => {
      const trackableError = toTrackableError(error);
      const viewerIsUsingAdminPowers = viewerIsWbAdminUsingPrivileges(viewer);

      trackWorkspaceErrorEvent({
        action,
        errorType,
        viewerIsUsingAdminPowers,
        viewType,
        error: trackableError,
      });

      // The reset click is already captured as a RUM action through the
      // button's `data-dd-action-name`; reporting it as an error too would
      // double-count the underlying failure, which is reported when the error
      // is first shown.
      if (action === 'Reset workspace') {
        return;
      }

      recordRumError(
        error instanceof Error ? error : new Error(trackableError.message),
        {
          'workspace.error_code': trackableError.errorCode ?? '',
          'workspace.error_type': errorType,
          'workspace.view_type': viewType ?? '',
          'workspace.viewer_is_using_admin_powers': viewerIsUsingAdminPowers,
        }
      );
    },
    [action, errorType, viewType, viewer]
  );

  return trackWorkspaceError;
}
